import {
  type CalendarGrid,
  type CalendarStyle,
  type DoorLogoPlacement,
  type DoorNumberStyle,
  FieldGrid,
} from "@countdown/theme";
import { type ReactElement, useId, useRef, useState } from "react";

import {
  NumeralControls,
  type PreviewLogo,
  SAMPLE_LOGO,
  present,
} from "@/components/CalendarDesign";
import { SaveForm } from "@/components/SaveForm";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { saveFieldDoorDesign } from "@/lib/api";
import { DEFAULT_DOOR_NUMBER, sameValue } from "@/lib/calendar-draft";
import { type DoorPoint, nearer, nudge, pointToPercent } from "@/lib/door-design";

/** Where a logo starts once it is placed by hand: about where the calendar draws it. */
const DEFAULT_LOGO_PLACE: DoorLogoPlacement = { x: 50, y: 55, size: 64 };

const STATES = [
  ["locked", "Geschlossen"],
  ["today", "Heute"],
  ["past_open", "Geöffnet"],
] as const;
type PreviewState = (typeof STATES)[number][0];
type Target = "number" | "logo";

const SINGLE: CalendarGrid = {
  columns: { mobile: 1, tablet: 1, desktop: 1 },
  tile_ratio: { width: 1, height: 1 },
};

export interface DoorDesignEditorProps {
  readonly actionId: string;
  readonly position: number;
  /** What this door stores of its own, `null` when it follows the action. */
  readonly own: DoorNumberStyle | null;
  /** This door's logo place, `null` for the built-in one. */
  readonly ownLogo: DoorLogoPlacement | null;
  /** The action's door number, `null` for the built-in look. */
  readonly actionStyle: DoorNumberStyle | null;
  /** The look of the action's doors, so the preview has the calendar's door. */
  readonly calendarStyle: CalendarStyle;
  readonly logo: PreviewLogo | null;
  readonly readOnly: boolean;
  readonly onSaved: () => void;
}

/**
 * One door's own number and logo place (D29, D31), set on a large preview of the door.
 *
 * The preview is the calendar's own tile. Over it lies one button: pressed and dragged, it
 * moves whichever of number and logo is closer to the pointer; focused, the arrow keys move
 * the chosen one by one per cent, with Shift by five. The sliders below say the same in
 * numbers, for everyone who neither drags nor uses arrow keys. Moving something makes it this
 * door's own; the check boxes take it back to the action's.
 */
export function DoorDesignEditor({
  actionId,
  position,
  own,
  ownLogo,
  actionStyle,
  calendarStyle,
  logo,
  readOnly,
  onSaved,
}: DoorDesignEditorProps): ReactElement {
  const id = useId();
  const [customNumber, setCustomNumber] = useState(own !== null);
  const [style, setStyle] = useState<DoorNumberStyle>(own ?? actionStyle ?? DEFAULT_DOOR_NUMBER);
  const [customLogo, setCustomLogo] = useState(ownLogo !== null);
  const [place, setPlace] = useState<DoorLogoPlacement>(ownLogo ?? DEFAULT_LOGO_PLACE);
  const [state, setState] = useState<PreviewState>("locked");
  const [target, setTarget] = useState<Target>("number");
  const dragging = useRef<Target | null>(null);

  // The closed door and today's door carry the number on the door; an open one in the frame.
  const part = state === "past_open" ? "open" : "door";
  const numberAt: DoorPoint = { x: style[part].x, y: style[part].y };

  const move = (which: Target, point: DoorPoint): void => {
    if (which === "number") {
      setCustomNumber(true);
      setStyle((current) => ({ ...current, [part]: { ...current[part], ...point } }));
    } else {
      setCustomLogo(true);
      setPlace((current) => ({ ...current, ...point }));
    }
  };

  const centre = (which: Target, axes: Partial<DoorPoint>): void => {
    move(which, { ...(which === "number" ? numberAt : place), ...axes });
  };

  const body = { style: customNumber ? style : null, logo: customLogo ? place : null };

  return (
    <SaveForm
      label={`Darstellung von Türchen ${String(position)}`}
      readOnly={readOnly}
      onSaved={onSaved}
      dirty={!sameValue(body, { style: own, logo: ownLogo })}
      submit={() => saveFieldDoorDesign(actionId, position, body)}
    >
      <div className="flex flex-wrap gap-2" role="group" aria-label="Zustand der Vorschau">
        {STATES.map(([value, label]) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={state === value ? "default" : "outline"}
            aria-pressed={state === value}
            onClick={() => {
              setState(value);
            }}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="mt-4 inline-block max-w-full border border-border bg-background p-8">
        <div className="relative w-72 max-w-full">
          <FieldGrid
            fields={[
              {
                position,
                presentation: present(state),
                logo: logo ?? SAMPLE_LOGO,
                numberStyle: customNumber ? style : actionStyle,
                logoPlacement: customLogo ? place : null,
              },
            ]}
            label={`Vorschau von Türchen ${String(position)}`}
            fieldTerm="door"
            layout="grid"
            grid={SINGLE}
            calendarStyle={calendarStyle}
          />
          <button
            type="button"
            className="absolute inset-0 cursor-move touch-none bg-transparent outline-offset-4 focus-visible:outline-2 focus-visible:outline-primary"
            aria-label={`${target === "number" ? "Zahl" : "Logo"} verschieben: ziehen oder Pfeiltasten, mit Umschalt in größeren Schritten`}
            onPointerDown={(event) => {
              const point = pointToPercent(
                event.currentTarget.getBoundingClientRect(),
                event.clientX,
                event.clientY,
              );
              const which = nearer(point, numberAt, place);
              event.currentTarget.setPointerCapture(event.pointerId);
              dragging.current = which;
              setTarget(which);
              move(which, point);
            }}
            onPointerMove={(event) => {
              if (dragging.current !== null) {
                move(
                  dragging.current,
                  pointToPercent(
                    event.currentTarget.getBoundingClientRect(),
                    event.clientX,
                    event.clientY,
                  ),
                );
              }
            }}
            onPointerUp={() => {
              dragging.current = null;
            }}
            onPointerCancel={() => {
              dragging.current = null;
            }}
            onKeyDown={(event) => {
              const next = nudge(target === "number" ? numberAt : place, event.key, event.shiftKey ? 5 : 1);
              if (next !== null) {
                event.preventDefault();
                move(target, next);
              }
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Pfeiltasten verschieben">
        <span className="text-sm">Pfeiltasten verschieben:</span>
        {(
          [
            ["number", "die Zahl"],
            ["logo", "das Logo"],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={target === value ? "default" : "outline"}
            aria-pressed={target === value}
            onClick={() => {
              setTarget(value);
            }}
          >
            {label}
          </Button>
        ))}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Ziehen fasst an, was näher am Zeiger liegt. Die Zahl wird für den gezeigten Zustand
        verschoben: geschlossen und heute auf der Tür, geöffnet im Rahmen. Die zufällige Neigung
        der Aktion kommt zur eigenen Neigung hinzu.
      </p>

      <div className="mt-5 flex items-center gap-2">
        <input
          id={`${id}-number`}
          type="checkbox"
          className="border border-input"
          checked={customNumber}
          onChange={(event) => {
            setCustomNumber(event.target.checked);
          }}
        />
        <Label htmlFor={`${id}-number`}>Eigene Darstellung der Zahl</Label>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Ohne Häkchen gilt die Türchenzahl der Aktion.
      </p>
      {customNumber ? (
        <>
          <Button
            className="mt-2"
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              centre("number", { x: 50, y: 50 });
            }}
          >
            Zahl zentrieren
          </Button>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <NumeralControls
              idPrefix={`${id}-door`}
              title="Auf der geschlossenen Tür"
              value={style.door}
              onChange={(door) => {
                setStyle({ ...style, door });
              }}
              checkContrast
            />
            <NumeralControls
              idPrefix={`${id}-open`}
              title="Im geöffneten Türchen"
              value={style.open}
              onChange={(open) => {
                setStyle({ ...style, open });
              }}
              checkContrast={false}
            />
          </div>
        </>
      ) : null}

      <div className="mt-5 flex items-center gap-2">
        <input
          id={`${id}-logo`}
          type="checkbox"
          className="border border-input"
          checked={customLogo}
          onChange={(event) => {
            setCustomLogo(event.target.checked);
          }}
        />
        <Label htmlFor={`${id}-logo`}>Logo selbst platzieren</Label>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Ohne Häkchen steht das Logo dort, wo der Kalender es von sich aus zeichnet.
      </p>
      {customLogo ? (
        <div className="mt-3 grid max-w-md gap-3">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Logo ausrichten">
            {(
              [
                ["Logo zentrieren", { x: 50, y: 50 }],
                ["Waagerecht mittig", { x: 50 }],
                ["Senkrecht mittig", { y: 50 }],
              ] as const
            ).map(([label, axes]) => (
              <Button
                key={label}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  centre("logo", axes);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          {(
            [
              ["x", "Position waagerecht (%)", 0, 100],
              ["y", "Position senkrecht (%)", 0, 100],
              ["size", "Größe (% des Türchens)", 20, 100],
            ] as const
          ).map(([key, label, min, max]) => (
            <div className="grid gap-1.5" key={key}>
              <Label htmlFor={`${id}-logo-${key}`}>
                {label}: {place[key]}
              </Label>
              <input
                id={`${id}-logo-${key}`}
                type="range"
                min={min}
                max={max}
                value={place[key]}
                onChange={(event) => {
                  setPlace({ ...place, [key]: Number(event.target.value) });
                }}
              />
            </div>
          ))}
        </div>
      ) : null}
    </SaveForm>
  );
}
